import classNames from 'classnames';
import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import TruncateTextWithButton from '../../../../../../molecules/merchant/common/buttons/TruncateTextWithButton';
import CourseDetail from './CourseDetail';

import css from './Course.module.scss';
import { MerchantBookingCourse } from '../../../../../../interfaces/merchants/bookings/courses';
import { RSButtonLink } from 'src/reservation-page/RSButtonLink';
import { RSButton } from 'src/reservation-page/RSButton';
import User from 'src/domain/entities/User';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import {
  EventSchemeResource,
  CourseSchemeResource,
} from 'src/interfaces/reservation_flow';

import { OptionsSelectModal } from 'src/templates/merchants/resources/book/course_type/components/options-select/OptionsSelectModal';
import { OptionsSelectModalWithLogin } from 'src/templates/merchants/resources/book/course_type/components/options-select/OptionsSelectModalWithLogin';
import { buildUrl } from 'src/utils/request-url';

interface Props {
  course: MerchantBookingCourse;
  href: string;
  resource: EventSchemeResource | CourseSchemeResource | undefined;
  user?: User;
}

const Course: React.FC<Props> = ({ course, href, resource, user }) => {
  const intl = useIntl();
  const classes = classNames(css.Course);

  const [isOptionsSelectModalOpen, setIsOptionsSelectModalOpen] =
    useState(false);
  const [
    isOptionsSelectModalWithLoginOpen,
    setIsOptionsSelectModalWithLoginOpen,
  ] = useState(false);

  const { name, image, price, description, duration } = course;

  const ButtonContent = useMemo(() => {
    // 組織管理者・スタッフアクセスの場合はresourceが存在しないので、予約ボタンのみ表示する
    if (!resource) {
      return (
        <div className="flex justify-end">
          <div className="pc:max-w-[200px] grow">
            <RSButtonLink isBlock={true} href={href} variant="outline">
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookReservation',
              })}
            </RSButtonLink>
          </div>
        </div>
      );
    }

    // ユーザがログインしているかつゲスト予約のみ以外の場合は、予約ボタンのみを表示する
    if (user && resource.acceptable_customer_login_status !== 'guest') {
      return (
        <div className="flex justify-end">
          <div className="pc:max-w-[200px] grow">
            {course.option_ids.length === 0 ? (
              <RSButtonLink isBlock={true} href={href} variant="outline">
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookReservation',
                })}
              </RSButtonLink>
            ) : (
              <RSButton
                isBlock={true}
                variant="outline"
                onClick={() => setIsOptionsSelectModalOpen(true)}
              >
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookReservation',
                })}
              </RSButton>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="pc:gap-1.5 flex justify-end gap-1">
        {resource.acceptable_customer_login_status === 'all' && (
          <div className="pc:max-w-[200px] flex-1 grow">
            {course.option_ids.length === 0 ? (
              <RSButtonLink isBlock={true} href={href} variant="outline">
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookGuestReservation',
                })}
              </RSButtonLink>
            ) : (
              <RSButton
                isBlock={true}
                variant="outline"
                onClick={() => setIsOptionsSelectModalOpen(true)}
              >
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookGuestReservation',
                })}
              </RSButton>
            )}
          </div>
        )}
        {resource.acceptable_customer_login_status === 'guest' && (
          <div className="pc:max-w-[200px] flex-1 grow">
            {course.option_ids.length === 0 ? (
              <RSButtonLink isBlock={true} href={href} variant="outline">
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookReservation',
                })}
              </RSButtonLink>
            ) : (
              <RSButton
                isBlock={true}
                variant="outline"
                onClick={() => setIsOptionsSelectModalOpen(true)}
              >
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookReservation',
                })}
              </RSButton>
            )}
          </div>
        )}
        {resource.acceptable_customer_login_status !== 'guest' && (
          <div className="pc:max-w-[200px] flex-1 grow">
            {course.option_ids.length === 0 ? (
              <RSButtonAnchor
                isBlock={true}
                href={buildUrl(`/signin/user?return_to=${href}`)}
                variant="outline"
              >
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookLoginReservation',
                })}
              </RSButtonAnchor>
            ) : (
              <RSButton
                isBlock={true}
                variant="outline"
                onClick={() => setIsOptionsSelectModalWithLoginOpen(true)}
              >
                {intl.formatMessage({
                  id: 'merchants.bookingPages.bookLoginReservation',
                })}
              </RSButton>
            )}
          </div>
        )}
      </div>
    );
  }, [resource, user, course.option_ids.length, href, intl]);

  return (
    <>
      <div className={classes}>
        <div className={css.Course__Header}>
          {image && image.thumb && image.thumb.url && (
            <img className={css.Course__Image} src={image.thumb.url} alt="" />
          )}
          <CourseDetail menuName={name} price={price} duration={duration} />
        </div>
        <div className={css.Course__Description}>
          <TruncateTextWithButton
            text={description}
            maxLine={7}
            noBottomMargin
          />
          <div className="pt-2">{ButtonContent}</div>
        </div>
      </div>

      {resource && course.option_ids.length > 0 && (
        <>
          <OptionsSelectModal
            isOpen={isOptionsSelectModalOpen}
            onRequireClosing={() => setIsOptionsSelectModalOpen(false)}
            merchantPublicId={resource.merchant_public_id}
            resourcePublicId={resource.public_id}
            courseCanonicalId={course.canonical_id}
            href={href}
          />

          <OptionsSelectModalWithLogin
            isOpen={isOptionsSelectModalWithLoginOpen}
            onRequireClosing={() => setIsOptionsSelectModalWithLoginOpen(false)}
            merchantPublicId={resource.merchant_public_id}
            resourcePublicId={resource.public_id}
            courseCanonicalId={course.canonical_id}
            href={href}
          />
        </>
      )}
    </>
  );
};

export default Course;
